import {
  GridPaginationModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTableFiltersPersistContext } from 'src/providers/table-filters-persist.provider';
import { paths } from 'src/routes/paths';
import { useMyDataStore } from 'src/store/me/me.store';
import { useProductsStore } from 'src/store/products/products.store';
import { useThemeStore } from 'src/store/products/theme.store';
import {
  BulkDeleteAffectedBenefit,
  CategoryStatus,
  categoryType,
  themeType,
} from 'src/types/products';
import { ThemesCategoriesFilterDraft } from './components/themes-categories-drawer-filters.hook';
import {
  getThemesCategoriesColumns,
  ThemesCategoriesRow,
} from './components/themes-categories-list-columns';

export type ThemesCategoriesTab = 'themes' | 'categories';

type TabTableState = {
  searchText: string;
  drawerFilters: ThemesCategoriesFilterDraft | null;
  sortModel: GridSortModel;
  paginationModel: GridPaginationModel;
};

type PersistedTableState = Partial<Record<ThemesCategoriesTab, TabTableState>>;

const TABS: Array<{ value: ThemesCategoriesTab; label: string; icon: string }> = [
  { value: 'themes', label: 'Temas', icon: 'lucide:palette' },
  { value: 'categories', label: 'Categorias', icon: 'lucide:tags' },
];

const STATUS_CHANGE_DEBOUNCE_MS = 700;

type DeletionImpact = {
  entity: ThemesCategoriesTab;
  ids: string[];
  affectedEntityNames: string[];
  affectedBenefitsCount: number;
  benefits: BulkDeleteAffectedBenefit[];
};

const createDefaultTableState = (): TabTableState => ({
  searchText: '',
  drawerFilters: null,
  sortModel: [],
  paginationModel: {
    page: 0,
    pageSize: 10,
  },
});

const restoreTableState = (state?: TabTableState): TabTableState => ({
  ...createDefaultTableState(),
  ...state,
  paginationModel: {
    ...createDefaultTableState().paginationModel,
    ...state?.paginationModel,
  },
});

export default function useThemesCategoriesList() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const currentTab: ThemesCategoriesTab = requestedTab === 'categories' ? 'categories' : 'themes';
  const permissions = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.benefits);
  const { updateFilters, getFilters } = useTableFiltersPersistContext();
  const persistedTableState = getFilters<PersistedTableState>([
    paths.dashboard.themeDetails(':id'),
    paths.dashboard.themeCreate,
    paths.dashboard.categoryDetails(':id'),
    paths.dashboard.categoryCreate,
  ]);

  const {
    loadingThemesList,
    loadingThemesBulkDelete,
    loadingThemesDeletionVerification,
    themesList,
    getThemesList,
    changeThemeStatus,
    deleteThemes,
    verifyThemesDeletion,
  } = useThemeStore();
  const {
    loadingCategoriesList,
    loadingCategoriesBulkDelete,
    loadingCategoriesDeletionVerification,
    categoriesList,
    getCategoriesList,
    changeCategoryStatus,
    deleteCategories,
    verifyCategoriesDeletion,
  } = useProductsStore();
  const [tableStateByTab, setTableStateByTab] = useState<
    Record<ThemesCategoriesTab, TabTableState>
  >(() => ({
    themes: restoreTableState(persistedTableState?.themes),
    categories: restoreTableState(persistedTableState?.categories),
  }));
  const [openFiltersDrawer, setOpenFiltersDrawer] = useState(false);
  const [selectedThemeIds, setSelectedThemeIds] = useState<GridRowSelectionModel>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<GridRowSelectionModel>([]);
  const [statusByEntityKey, setStatusByEntityKey] = useState<Record<string, CategoryStatus>>({});
  const [deletionImpact, setDeletionImpact] = useState<DeletionImpact | null>(null);
  const [isDeletionFlowLoading, setIsDeletionFlowLoading] = useState(false);
  const isHandlingHeaderSelectionRef = useRef(false);
  const deletionVerificationInProgressRef = useRef(false);
  const deletionInProgressRef = useRef(false);
  const statusChangeTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const statusBeforeChangeRef = useRef<Record<string, CategoryStatus>>({});
  const activeTableState = tableStateByTab[currentTab];
  const {
    searchText,
    drawerFilters: appliedFilters,
    sortModel,
    paginationModel,
  } = activeTableState;

  useEffect(() => {
    if (currentTab === 'themes') {
      getThemesList();
      return;
    }

    getCategoriesList();
  }, [currentTab, getCategoriesList, getThemesList]);

  useEffect(() => {
    updateFilters(JSON.stringify(tableStateByTab));
  }, [tableStateByTab, updateFilters]);

  useEffect(
    () => () => {
      Object.values(statusChangeTimersRef.current).forEach(clearTimeout);
      statusChangeTimersRef.current = {};
    },
    []
  );

  const handleChangeTab = (newTab: ThemesCategoriesTab) => {
    setSearchParams((currentSearchParams) => {
      const nextSearchParams = new URLSearchParams(currentSearchParams);
      nextSearchParams.set('tab', newTab);
      return nextSearchParams;
    });
  };

  const sourceData: Array<themeType | categoryType> =
    currentTab === 'themes' ? themesList : categoriesList;

  const rows = useMemo<ThemesCategoriesRow[]>(
    () =>
      sourceData.map((item, index) => {
        const entityId = String((item as themeType & categoryType & { _id?: string })._id || '');
        const entityKey = `${currentTab}:${entityId}`;
        const status = entityId ? statusByEntityKey[entityKey] || item.status : item.status;

        return {
          id: entityId || `${currentTab}-${index}-${item.name}`,
          entityId,
          name: item.name,
          status,
          statusLabel: status === 'active' ? 'Ativo' : 'Inativo',
        };
      }),
    [currentTab, sourceData, statusByEntityKey]
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLocaleLowerCase('pt-BR');
    const statuses = appliedFilters?.status || [];

    return rows.filter((row) => {
      const matchesSearch =
        !normalizedSearch || row.name.toLocaleLowerCase('pt-BR').includes(normalizedSearch);
      const matchesStatus = !statuses.length || statuses.includes(row.status);

      return matchesSearch && matchesStatus;
    });
  }, [appliedFilters, rows, searchText]);

  const sortedRows = useMemo(() => {
    const sort = sortModel[0];

    if (!sort?.sort) {
      return filteredRows;
    }

    return [...filteredRows].sort((first, second) => {
      const firstValue = String(first[sort.field as keyof ThemesCategoriesRow] || '');
      const secondValue = String(second[sort.field as keyof ThemesCategoriesRow] || '');
      const comparison = firstValue.localeCompare(secondValue, 'pt-BR', {
        numeric: true,
        sensitivity: 'base',
      });

      return sort.sort === 'asc' ? comparison : -comparison;
    });
  }, [filteredRows, sortModel]);

  const paginatedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return sortedRows.slice(start, start + paginationModel.pageSize);
  }, [paginationModel, sortedRows]);

  const handleChangeStatus = useCallback(
    (entityId: string, status: CategoryStatus) => {
      const entityType = currentTab;
      const entityKey = `${entityType}:${entityId}`;
      const previousStatus = rows.find((row) => row.entityId === entityId)?.status;

      if (!previousStatus) {
        return;
      }

      if (!Object.prototype.hasOwnProperty.call(statusBeforeChangeRef.current, entityKey)) {
        statusBeforeChangeRef.current[entityKey] = previousStatus;
      }

      const statusBeforeChange = statusBeforeChangeRef.current[entityKey];

      setStatusByEntityKey((current) => {
        const next = { ...current };

        if (status === statusBeforeChange) {
          delete next[entityKey];
        } else {
          next[entityKey] = status;
        }

        return next;
      });

      if (statusChangeTimersRef.current[entityKey]) {
        clearTimeout(statusChangeTimersRef.current[entityKey]);
      }

      if (status === statusBeforeChange) {
        delete statusBeforeChangeRef.current[entityKey];
        delete statusChangeTimersRef.current[entityKey];
        return;
      }

      statusChangeTimersRef.current[entityKey] = setTimeout(async () => {
        const rollbackStatus = statusBeforeChangeRef.current[entityKey];

        delete statusChangeTimersRef.current[entityKey];

        try {
          if (entityType === 'themes') {
            await changeThemeStatus(entityId, status);
          } else {
            await changeCategoryStatus(entityId, status);
          }

          delete statusBeforeChangeRef.current[entityKey];
          setStatusByEntityKey((current) => {
            const next = { ...current };
            delete next[entityKey];
            return next;
          });
          enqueueSnackbar(
            `Status ${entityType === 'themes' ? 'do tema' : 'da categoria'} atualizado com sucesso!`,
            { variant: 'success' }
          );
        } catch (error: any) {
          setStatusByEntityKey((current) => ({
            ...current,
            [entityKey]: rollbackStatus,
          }));
          delete statusBeforeChangeRef.current[entityKey];
          enqueueSnackbar(
            error?.message ||
              `Erro ao atualizar status ${entityType === 'themes' ? 'do tema' : 'da categoria'}.`,
            { variant: 'error' }
          );
        }
      }, STATUS_CHANGE_DEBOUNCE_MS);
    },
    [changeCategoryStatus, changeThemeStatus, currentTab, enqueueSnackbar, rows]
  );

  const columns = useMemo(
    () =>
      getThemesCategoriesColumns({
        currentTab,
        canUpdateStatus: Boolean(permissions?.update),
        handleChangeStatus,
      }),
    [currentTab, handleChangeStatus, permissions?.update]
  );

  const handleSearchTextChange = useCallback(
    (value: string) => {
      setTableStateByTab((current) => ({
        ...current,
        [currentTab]: {
          ...current[currentTab],
          searchText: value,
          paginationModel: { ...current[currentTab].paginationModel, page: 0 },
        },
      }));
    },
    [currentTab]
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setTableStateByTab((current) => ({
        ...current,
        [currentTab]: {
          ...current[currentTab],
          sortModel: model,
          paginationModel: { ...current[currentTab].paginationModel, page: 0 },
        },
      }));
    },
    [currentTab]
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setTableStateByTab((current) => ({
        ...current,
        [currentTab]: {
          ...current[currentTab],
          paginationModel: model,
        },
      }));
    },
    [currentTab]
  );

  const handleFilterButtonClick = useCallback(() => {
    setOpenFiltersDrawer(true);
  }, []);

  const onCloseFilters = useCallback(() => {
    setOpenFiltersDrawer(false);
  }, []);

  const onApplyFilters = useCallback(
    (filters: ThemesCategoriesFilterDraft) => {
      setTableStateByTab((current) => ({
        ...current,
        [currentTab]: {
          ...current[currentTab],
          drawerFilters: filters,
          paginationModel: { ...current[currentTab].paginationModel, page: 0 },
        },
      }));
    },
    [currentTab]
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<ThemesCategoriesRow>) => {
      const { entityId } = params.row;

      if (!entityId) {
        return;
      }

      navigate(
        currentTab === 'themes'
          ? paths.dashboard.themeDetails(entityId)
          : paths.dashboard.categoryDetails(entityId)
      );
    },
    [currentTab, navigate]
  );

  const onCategorySelectionModelChange = useCallback((model: GridRowSelectionModel) => {
    if (isHandlingHeaderSelectionRef.current) {
      return;
    }

    setSelectedCategoryIds(model.map((id) => String(id)));
  }, []);

  const onCategoryHeaderSelectionChange = useCallback(
    (checked: boolean) => {
      isHandlingHeaderSelectionRef.current = true;
      setSelectedCategoryIds(
        checked
          ? sortedRows.map((row) => row.entityId).filter((id): id is string => Boolean(id))
          : []
      );

      setTimeout(() => {
        isHandlingHeaderSelectionRef.current = false;
      }, 0);
    },
    [sortedRows]
  );

  const onThemeSelectionModelChange = useCallback((model: GridRowSelectionModel) => {
    if (isHandlingHeaderSelectionRef.current) {
      return;
    }

    setSelectedThemeIds(model.map((id) => String(id)));
  }, []);

  const onThemeHeaderSelectionChange = useCallback(
    (checked: boolean) => {
      isHandlingHeaderSelectionRef.current = true;
      setSelectedThemeIds(
        checked
          ? sortedRows.map((row) => row.entityId).filter((id): id is string => Boolean(id))
          : []
      );

      setTimeout(() => {
        isHandlingHeaderSelectionRef.current = false;
      }, 0);
    },
    [sortedRows]
  );

  const executeBulkDelete = useCallback(
    async (entity: ThemesCategoriesTab, ids: string[]) => {
      if (!ids.length || deletionInProgressRef.current) {
        return;
      }

      deletionInProgressRef.current = true;

      try {
        const result = entity === 'themes' ? await deleteThemes(ids) : await deleteCategories(ids);

        ids.forEach((id) => {
          const entityKey = `${entity}:${id}`;

          if (statusChangeTimersRef.current[entityKey]) {
            clearTimeout(statusChangeTimersRef.current[entityKey]);
          }
          delete statusChangeTimersRef.current[entityKey];
          delete statusBeforeChangeRef.current[entityKey];
        });

        setStatusByEntityKey((current) => {
          const next = { ...current };
          ids.forEach((id) => delete next[`${entity}:${id}`]);
          return next;
        });

        if (entity === 'themes') {
          setSelectedThemeIds([]);
          await getThemesList();
        } else {
          setSelectedCategoryIds([]);
          await getCategoriesList();
        }

        setDeletionImpact(null);
        enqueueSnackbar(
          result.deletedCount === 1
            ? `${entity === 'themes' ? 'Tema excluído' : 'Categoria excluída'} com sucesso!`
            : `${result.deletedCount} ${entity === 'themes' ? 'temas excluídos' : 'categorias excluídas'} com sucesso!`,
          { variant: 'success' }
        );
      } catch (error: any) {
        enqueueSnackbar(
          error?.message ||
            `Erro ao excluir ${entity === 'themes' ? 'os temas' : 'as categorias'}.`,
          { variant: 'error' }
        );
      } finally {
        deletionInProgressRef.current = false;
      }
    },
    [deleteCategories, deleteThemes, enqueueSnackbar, getCategoriesList, getThemesList]
  );

  const onRequestBulkDelete = useCallback(async () => {
    const entity = currentTab;
    const ids = (entity === 'themes' ? selectedThemeIds : selectedCategoryIds).map((id) =>
      String(id)
    );

    if (!ids.length || deletionVerificationInProgressRef.current) {
      return;
    }

    deletionVerificationInProgressRef.current = true;
    setIsDeletionFlowLoading(true);

    try {
      const verification =
        entity === 'themes' ? await verifyThemesDeletion(ids) : await verifyCategoriesDeletion(ids);
      const benefits: BulkDeleteAffectedBenefit[] = [
        ...(verification.products || []).map((benefit) => ({
          ...benefit,
          type: 'product' as const,
        })),
        ...(verification.coupons || []).map((benefit) => ({
          ...benefit,
          type: 'coupon' as const,
        })),
      ].slice(0, 3);
      const affectedBenefitsCount = Number(verification.affectedBenefitsCount || 0);

      if (affectedBenefitsCount === 0) {
        await executeBulkDelete(entity, ids);
        return;
      }

      let affectedEntityIds: string[] = [];

      if (entity === 'themes' && 'themeIds' in verification) {
        affectedEntityIds = verification.themeIds;
      }

      if (entity === 'categories' && 'categoryIds' in verification) {
        affectedEntityIds = verification.categoryIds;
      }
      const entityList = entity === 'themes' ? themesList : categoriesList;
      const affectedEntityNames = affectedEntityIds.map(
        (id) => entityList.find((item) => item._id === id)?.name || id
      );

      setDeletionImpact({
        entity,
        ids,
        affectedEntityNames,
        affectedBenefitsCount,
        benefits,
      });
    } catch (error: any) {
      enqueueSnackbar(
        error?.message ||
          `Erro ao verificar a exclusão ${entity === 'themes' ? 'dos temas' : 'das categorias'}.`,
        { variant: 'error' }
      );
    } finally {
      deletionVerificationInProgressRef.current = false;
      setIsDeletionFlowLoading(false);
    }
  }, [
    categoriesList,
    currentTab,
    enqueueSnackbar,
    executeBulkDelete,
    selectedCategoryIds,
    selectedThemeIds,
    themesList,
    verifyCategoriesDeletion,
    verifyThemesDeletion,
  ]);

  const isDeletingImpact =
    deletionImpact?.entity === 'themes'
      ? loadingThemesBulkDelete
      : deletionImpact?.entity === 'categories' && loadingCategoriesBulkDelete;

  const onCloseDeletionImpact = useCallback(() => {
    if (!isDeletingImpact) {
      setDeletionImpact(null);
    }
  }, [isDeletingImpact]);

  const onConfirmDeletionImpact = useCallback(() => {
    if (deletionImpact) {
      executeBulkDelete(deletionImpact.entity, deletionImpact.ids);
    }
  }, [deletionImpact, executeBulkDelete]);

  const selectedFiltersCount = appliedFilters?.status.length ? 1 : 0;
  const isBulkDeleteLoading =
    isDeletionFlowLoading ||
    loadingThemesDeletionVerification ||
    loadingCategoriesDeletionVerification ||
    loadingThemesBulkDelete ||
    loadingCategoriesBulkDelete;

  return {
    TABS,
    currentTab,
    searchText,
    sortModel,
    paginationModel,
    openFiltersDrawer,
    appliedFilters,
    columns,
    paginatedRows,
    totalRowsCount: filteredRows.length,
    filteredRowsCount: filteredRows.length,
    selectedFiltersCount,
    selectedThemeIds,
    selectedCategoryIds,
    deletionImpact,
    isTableLoading: currentTab === 'themes' ? loadingThemesList : loadingCategoriesList,
    isBulkDeleteLoading,
    isBulkDeleteDisabled: isBulkDeleteLoading || Boolean(deletionImpact),
    isDeletingImpact,
    canCreate: Boolean(permissions?.create),
    canDelete: Boolean(permissions?.delete),
    handleChangeTab,
    handleSearchTextChange,
    handleSortModelChange,
    handleFilterButtonClick,
    onCloseFilters,
    onApplyFilters,
    handleRowClick,
    onCategorySelectionModelChange,
    onCategoryHeaderSelectionChange,
    onThemeSelectionModelChange,
    onThemeHeaderSelectionChange,
    onRequestBulkDelete,
    onCloseDeletionImpact,
    onConfirmDeletionImpact,
    setPaginationModel: handlePaginationModelChange,
    navigate,
  };
}
